import { addHours } from 'date-fns'
import { ArrowRightIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { useMemo } from 'react'
import { toast } from 'sonner'

import { EmployerOnboardingPage, useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { PayrollConnectionPicker } from 'components/payroll/PayrollConnectionPicker'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Button, DropdownMenu, useBreakpoint, usePageTitle } from 'slab'
import { getBrowserTimezoneDatetimeString } from 'utils'
import { useGetQuotingPayrollProviderConfigs as useGetPayrollProviderConfigs, usePostPayrollManageDisconnect } from 'utils/web_api'

import { CenteredLayout } from '../CenteredLayout'
import { useEmployer } from '../EmployerProvider'
import { emitEmployerOnboardingEvent } from '../utils'
import { usePayrollConfig } from './PayrollConfigProvider'

function formatSyncDate (nextSyncDate: string | null, lastSyncDate: string | null) {
  if (lastSyncDate) {
    return `Last synced on ${getBrowserTimezoneDatetimeString(new Date(lastSyncDate))}`
  } else {
    // Next sync date assumes Merge/ADP/Finch automated connections. Finch assisted can take up to a week to sync.
    // If we ever bring back support for Finch assisted, we'll need to update this.
    if (nextSyncDate) {
      return `First sync by ${getBrowserTimezoneDatetimeString(new Date(nextSyncDate))}`
    } else {
      const estimatedDate = addHours(new Date(), 24)
      return `First sync by ${getBrowserTimezoneDatetimeString(estimatedDate)}`
    }
  }
}

export function PayrollPage () {
  usePageTitle('Connect to payroll')

  const { refetch: refetchEmployer } = useEmployer()
  const { payrollConfig, refetch: refetchPayrollConfig } = usePayrollConfig()
  const { breakpointIsAtLeast } = useBreakpoint()
  const { navigateToPreviousStep, navigateToNextStep, navigateToStep } = useOnboardingStep()

  const {
    data: providerConfigs,
    isLoading: isLoadingProviders
  } = useGetPayrollProviderConfigs({
    query: {
      enabled: !payrollConfig?.connected_to_payroll,
    },
  })

  const disconnectMutation = usePostPayrollManageDisconnect()

  const payrollProviders = providerConfigs?.data || []
  const promotedProviders = useMemo(
    () =>
      payrollProviders.filter((provider) => provider.promoted_in_onboarding) ||
      [],
    [payrollProviders]
  )

  function renderPayrollPicker () {
    if (payrollConfig?.connected_to_payroll) {
      return null
    }

    if (isLoadingProviders) {
      return <LoadingSpinnerCentered />
    }

    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <PayrollConnectionPicker
          payrollProviders={payrollProviders}
          promotedPayrollProviders={promotedProviders}
          onSuccessCallback={async ({ supportsAutomatedDeductions, supportsPaySchedules }) => {
            emitEmployerOnboardingEvent('employeronboarding_connect_payroll', {
              version: 'neo'
            })

            await refetchEmployer()
            await refetchPayrollConfig()

            if (supportsAutomatedDeductions) {
              navigateToStep(EmployerOnboardingPage.AUTOMATIC_DEDUCTIONS)
            } else if (!supportsPaySchedules) {
              navigateToStep(EmployerOnboardingPage.PAY_SCHEDULE)
            } else {
              navigateToStep(EmployerOnboardingPage.REMAINING_INFO)
            }
          }}
          // eslint-disable-next-line react/jsx-handler-names
          onSuccessUrl={window.location.href}
        />
        <div className='text-center mt-6'>
          <Button
            variant='tertiary-deemphasized'
            onClick={() => navigateToStep(EmployerOnboardingPage.PAY_SCHEDULE)}
          >
            Skip
          </Button>
        </div>
      </motion.div>
    )
  }

  function renderPayrollConnection () {
    if (!payrollConfig?.connected_to_payroll) {
      return null
    }

    return (
      <div className='flex flex-col sm:flex-row gap-4 sm:items-center justify-between'>
        <div className='flex flex-row gap-4 items-center'>
          <div className='w-10 h-10 rounded-full overflow-hidden flex-shrink-0'>
            <img
              src={payrollConfig.provider_icon_url || ''}
              alt={`${payrollConfig.provider_display_name} logo`}
              className='w-full h-full object-cover border rounded-full'
            />
          </div>
          <div>
            <div className='flex gap-1 items-center'>
              <div className='size-2.5 rounded-full bg-green-400 flex-shrink-0' />
              <span className='body text-gray-800'>
                Synced from{' '}
                {payrollConfig.provider_display_name || 'your payroll provider'}
              </span>
            </div>
            <div className='body-sm'>
              {formatSyncDate(payrollConfig.next_employee_sync, payrollConfig.last_employee_sync)}
            </div>
          </div>
        </div>
        {!payrollConfig.shared_connection && (
          <DropdownMenu
            trigger={({ open }) => (
              <Button
                variant='secondary'
                width={breakpointIsAtLeast('sm') ? 'auto' : 'full'}
                rightIcon={open ? ChevronUpIcon : ChevronDownIcon}
              >
                Manage
              </Button>
            )}
          >
            <DropdownMenu.Item
              type='destructive'
              onSelect={async () => {
                try {
                  await disconnectMutation.mutateAsync()
                  await refetchPayrollConfig()
                  await refetchEmployer()
                  toast.success('Successfully disconnected from payroll provider')
                } catch {
                  toast.error('Failed to disconnect from payroll provider')
                }
              }}
              disabled={disconnectMutation.isPending}
            >
              {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
            </DropdownMenu.Item>
          </DropdownMenu>
        )}
      </div>
    )
  }

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header
        heading='Connect and sync your employee directory'
        subheading='By connecting your payroll, you can easily invite employees to Thatch and manage payroll deductions for any out-of-pocket costs.'
      />
      <CenteredLayout.Container>
        {renderPayrollPicker()}
        {renderPayrollConnection()}
      </CenteredLayout.Container>
      <CenteredLayout.Buttons>
        <Button
          variant='secondary'
          onClick={navigateToPreviousStep}
        >
          Back
        </Button>
        {payrollConfig?.connected_to_payroll && (
          <Button
            variant='primary'
            onClick={navigateToNextStep}
            rightIcon={ArrowRightIcon}
          >
            Continue
          </Button>
        )}
      </CenteredLayout.Buttons>
    </CenteredLayout>
  )
}
